import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Links, { LinkItem } from '../../common/Widgets/Links';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import { DefaultRadioOptions, PageAbsolutePath } from '../../../consts/Common';
import { useContext, useEffect, useState } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { setCountryCode } from '../../common/HelperMethods';
import { InsuranceJourneyContext } from '../../common/CommonTypes';
import FairUseNoticePage from './FairUseNoticePage';
import BusinessTermsAncillaryPage from './BusinessTermsAncillaryPage';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ButtonBack } from '../../common/Button/ButtonBack';

const pdfIcon = <FontAwesomeIcon icon={faFilePdf} />;

const AboutTermsOfBusinessAndFairUsePage = (): JSX.Element => {
  const { site } = useContext(SiteContext);
  const navigate = useNavigate();

  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { aboutTermsOfBusinessAndFairUseDetails } = journey;
  const { readAndAgreeWithTerms, consentForDetailsToBePassed } =
    aboutTermsOfBusinessAndFairUseDetails;

  const { aboutTermsOfBusinessAndFairUseDetailsPage, commonEvents } =
    pageEvents;
  const { onChangeReadAndAgreeWithTerms, onChangeConsentForDetailsToBePassed } =
    aboutTermsOfBusinessAndFairUseDetailsPage;
  const { onPageChange } = commonEvents;
  const [componentToRender, setComponentToRender] = useState<
    JSX.Element | undefined
  >(undefined);
  const [modalTitle, setModalTitle] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const linkItems: LinkItem[] = [
    {
      to: undefined,
      text: 'Terms of Business',
      onClick: () => {
        setModalTitle('Terms of Business');
        setComponentToRender(<BusinessTermsAncillaryPage isShowingInPopup />);
      }
    },
    {
      to: undefined,
      text: 'Fair Use Notice',
      onClick: () => {
        setModalTitle('Fair Use Notice');
        setComponentToRender(<FairUseNoticePage isShowingInPopup />);
      }
    }
  ];

  const onNextClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.ImportantNoticeInformation,
      site.countryCode
    );
    navigate(path);
    onPageChange(PageAbsolutePath.Insurance.YourQuote, true);
  };

  const onBackClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.CheckYourDetails,
      site.countryCode
    );
    navigate(path);
    onPageChange(PageAbsolutePath.Insurance.YourQuote, true);
  };

  const closePopup = () => {
    setComponentToRender(undefined);
  };

  return (
    <div className='no-stepper'>
      <h5 className='section-header m3-top'>
        Paying monthly: About our terms of business and fair use notice
      </h5>

      <p>
        Opting to pay for your annual insurance via Direct Debit means that you
        are applying for a finance agreement. To apply for this finance, we will
        need to pass your details to the lender, Premium Credit Limited. We do
        not charge a fee for introducing you, but we do receive remuneration
        from Premium Credit Limited in the form of commission for our credit
        broking arrangements with our customers.
      </p>

      <p>
        These are our standard Terms of Business and Fair Use Notice which
        describe the services we provide and how your personal information will
        be used. You should read both documents carefully and contact us on{' '}
        {site.brand.brandPhoneNumbers.quotesAndRenewalsNumber} if you do not
        understand any points. By ticking the box below, you confirm you are
        happy for your details to be passed to Premium Credit Limited and that
        you have read the information and agree to its content.
      </p>

      <p>
        These documents will open in a new pop-up window. You must view the
        documents before proceeding. If they are not opening, you may have
        pop-ups blocked on your device and will need to adjust your settings to
        allow this. If you need any help, please contact us.
      </p>

      <div className='document-links'>
        <Links
          id={'terms-of-business-and-fair-usage-notice'}
          icon={pdfIcon}
          items={linkItems}
        />
      </div>
      <div className='one-col'>
        <div className='max-content'>
          <RadioOptions
            errorMessage=''
            selectedOption={
              readAndAgreeWithTerms === null
                ? ''
                : readAndAgreeWithTerms
                  ? 'Yes'
                  : 'No'
            }
            optionsList={DefaultRadioOptions}
            setSelectedOption={(value) =>
              onChangeReadAndAgreeWithTerms(value === 'Yes')
            }
            label={
              'I have read and agree with the Terms of Business and Fair Use Notice.'
            }
            name='readAndAgreeWithTerms'
          />
        </div>
      </div>

      <div className='one-col'>
        <div className='max-content'>
          <RadioOptions
            errorMessage=''
            selectedOption={
              consentForDetailsToBePassed === null
                ? ''
                : consentForDetailsToBePassed
                  ? 'Yes'
                  : 'No'
            }
            optionsList={DefaultRadioOptions}
            setSelectedOption={(value) =>
              onChangeConsentForDetailsToBePassed(value === 'Yes')
            }
            label={
              'I consent for my details to be passed to Premium Credit Limited to apply for finance to pay for the insurance policy.'
            }
            name='consentForDetailsToBePassed'
          />
        </div>
      </div>
      <div className='flex-row form-nav'>
        <ButtonBack
          name='btn-back'
          text='Back'
          onClick={onBackClick}
          additionalClassNames='btn-back'
        />
        {readAndAgreeWithTerms && consentForDetailsToBePassed && (
          <ButtonContinue
            name='btn-Next'
            text='Next'
            onClick={onNextClick}
            additionalClassNames='btn-continue'
          />
        )}
      </div>

      <Modal
        className={`insurance-${site.brand.brandName} modal-wide-component`}
        toggle={closePopup}
        role='modal-popup'
        isOpen={componentToRender !== undefined}>
        <ModalHeader toggle={closePopup}>{modalTitle}</ModalHeader>
        <ModalBody>
          <div className='ancillary-page'>
            <div className='ancillary-main'>
              <div className=''>{componentToRender}</div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='modal-btn-continue'
            text='Close'
            onClick={closePopup}
            additionalClassNames='btn-ok'
          />
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AboutTermsOfBusinessAndFairUsePage;
